import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Crown, Clock, Wifi, IndianRupee, UserPlus } from "lucide-react";
import { api, errorMessage } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="rounded-2xl border bg-card p-6">
    <div className="flex items-center justify-between"><p className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{label}</p><Icon size={16} className={accent || "text-muted-foreground"} /></div>
    <p data-testid={testId} className="mt-3 font-mono text-3xl font-semibold">{value ?? "—"}</p>
  </div>
);

export function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/analytics").then(({ data }) => setA(data)).catch((err) => toast.error(errorMessage(err)));
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  const total = a?.total_users || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="rounded-2xl topo-bg grain text-white p-8 relative overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p className="data-label text-emerald-400 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> Live presence</p>
            <p data-testid="admin-live-users-count" className="mt-3 font-mono text-6xl font-semibold leading-none">{a?.online_users ?? "—"}</p>
            <p className="text-slate-400 text-sm mt-2">users online now (heartbeat within {a?.online_window_seconds ?? 60}s)</p>
          </div>
          <div className="text-right text-sm text-slate-300">
            <p>Site mode: <span data-testid="admin-analytics-site-mode" className="font-mono uppercase text-emerald-300">{a?.site_mode}</span></p>
            <p>Price: <span className="font-mono">₹{a?.price_inr}</span>/month</p>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat icon={Users} label="Total users" value={a?.total_users} testId="admin-total-users" />
        <Stat icon={Users} label="Free" value={a?.free_users} testId="admin-free-users" />
        <Stat icon={Crown} label="Premium" value={a?.premium_users} testId="admin-premium-users" accent="text-emerald-600" />
        <Stat icon={Clock} label="On trial" value={a?.trial_users} testId="admin-trial-users" accent="text-amber-600" />
        <Stat icon={UserPlus} label="New (7d)" value={a?.new_users_7d} testId="admin-new-users" />
        <Stat icon={IndianRupee} label="Revenue" value={a ? `₹${a.revenue_inr}` : null} testId="admin-revenue" accent="text-emerald-600" />
      </div>
      <div className="rounded-2xl border bg-card p-6">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-4">Tier breakdown</p>
        <div className="h-3 rounded-full overflow-hidden flex bg-muted">
          <div className="bg-emerald-600" style={{ width: `${pct(a?.premium_users || 0)}%` }} />
          <div className="bg-amber-500" style={{ width: `${pct(a?.trial_users || 0)}%` }} />
          <div className="bg-slate-400" style={{ width: `${pct(a?.free_users || 0)}%` }} />
        </div>
        <div className="mt-3 flex flex-wrap gap-5 text-xs">
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-emerald-600 inline-block" />Premium {pct(a?.premium_users || 0)}%</span>
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" />Trial {pct(a?.trial_users || 0)}%</span>
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-slate-400 inline-block" />Free {pct(a?.free_users || 0)}%</span>
          <span className="text-muted-foreground ml-auto flex items-center gap-1"><Wifi size={12} /> paused {a?.paused_users ?? 0} · cancelled {a?.cancelled_users ?? 0} · lapsed {a?.lapsed_users ?? 0}</span>
        </div>
      </div>
    </div>
  );
}
